import { useEffect, useRef, useState } from "react";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { Box, FormControlLabel, Switch, Typography, IconButton, Paper, Tooltip } from "@mui/material";
import { globalTheme } from "../../styles";
import { Drawer } from "../Drawer";
import { createWebSocketConnection, closeWebSocketConnection } from "../../hooks";
import { WEBSOCKET_CONNECTED_KEY, WEBSOCKET_URL } from "../../constants";

export const PrinterGuiDrawer = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isToggledOn, setIsToggledOn] = useState(false);
  const [printerStatus, setPrinterStatus] = useState("disconnected");
  const [errorMessage, setErrorMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const websocketRef = useRef(null);

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleToggleChange = (event) => {
    const checked = event.target.checked;
    setIsToggledOn(checked);
  };

  const openWebSocketInBackground = () => {
    setPrinterStatus("connecting");

    websocketRef.current = createWebSocketConnection(
      WEBSOCKET_URL,
      (message) => {},
      () => {
        setPrinterStatus("connected");
        setErrorMessage("Error");
        setRetryCount(0);
      },
      (error) => {
        setPrinterStatus("error");
        setErrorMessage(`WebSocket error: ${error}`);
        setRetryCount((prevCount) => prevCount + 1);
        setIsToggledOn(false);
      },
      () => {
        setPrinterStatus("disconnected");
        setIsToggledOn(false);
      }
    );
  };

  const handleCloseWebSocket = () => {
    if (websocketRef.current) {
      closeWebSocketConnection(websocketRef.current);
      setPrinterStatus("disconnected");
      setRetryCount(0);
      setIsToggledOn(false);
    }
  };

  const checkWebSocketStatus = () => {
    const websocketConnected = localStorage.getItem(WEBSOCKET_CONNECTED_KEY) === "true";
    if (websocketConnected) {
      setIsToggledOn(true);
      setPrinterStatus("connected");
    } else {
      setIsToggledOn(false);
      setPrinterStatus("disconnected");
    }
  };

  useEffect(() => {
    checkWebSocketStatus();
    const intervalId = setInterval(checkWebSocketStatus, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isToggledOn) {
      openWebSocketInBackground();
    } else {
      handleCloseWebSocket();
    }
  }, [isToggledOn]);

  const getIconColor = () => {
    if (retryCount > 0) {
      return globalTheme.error;
    }

    switch (printerStatus) {
      case "connected":
        return globalTheme.activeMenu;
      case "disconnected":
        return globalTheme.secondaryColor;
      case "error":
        return globalTheme.error;
      case "connecting":
        return globalTheme.connecting;
      default:
        return globalTheme.secondaryColor;
    }
  };

  const getToggleLabel = () => {
    switch (printerStatus) {
      case "connected":
        return "PrinterGUI Connected";
      case "disconnected":
        return "PrinterGUI Disconnected";
      case "error":
        return "Printer GUI ConnectionError";
      case "connecting":
        return "Connecting...";
      default:
        return "disconnected";
    }
  };

  return (
    <Box sx={{ cursor: "pointer" }}>
      <Tooltip title={getToggleLabel()} arrow>
        <PrintIcon onClick={handleOpenDrawer} style={{ color: getIconColor() }} />
      </Tooltip>
      <Drawer open={isDrawerOpen} onClose={handleCloseDrawer}>
        <Box sx={{ width: "100%" }} role="presentation">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1 }}>
            <Typography variant="h6">Print Preference</Typography>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Paper
            elevation={3}
            sx={{ borderRadius: "10px", mt: 2, mx: 2, height: "60px", display: "flex", alignItems: "center", px: 2 }}
          >
            <FormControlLabel
              control={<Switch checked={isToggledOn} onChange={handleToggleChange} color="primary" />}
              label={getToggleLabel()}
            />
          </Paper>
        </Box>
      </Drawer>
    </Box>
  );
};
